import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { logout } from '@/store/slices/authSlice.ts';
import { getRexSSOLogoutUrl } from '@/api/core/apiConfig';
import { clearStoredAuth } from '@/utils/storage';
import { ROUTE_DEFINITIONS } from '@/routes';


export const useMenuNavigation = () => {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();
  const dispatch = useAppDispatch();
  const isSSO = useAppSelector((state) => state.auth.isSSO);
  
  // Estados del menú
  const [activeMenuItem, setActiveMenuItem] = useState<number | null>(null);
  const [isConfigMenuOpen, setIsConfigMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [isAppLauncherOpen, setIsAppLauncherOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Refs para los timers
  const menuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const configCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Función para limpiar timers
  const clearMenuTimer = useCallback(() => {
    if (menuCloseTimer.current) {
      clearTimeout(menuCloseTimer.current);
      menuCloseTimer.current = null;
    }
  }, []);

  const clearConfigTimer = useCallback(() => {
    if (configCloseTimer.current) {
      clearTimeout(configCloseTimer.current);
      configCloseTimer.current = null;
    }
  }, []);

  // Handlers mejorados con delay
  const handleMenuItemEnter = useCallback((itemId: number) => {
    clearMenuTimer();
    setActiveMenuItem(itemId);
  }, [clearMenuTimer]);

  const handleMenuItemLeave = useCallback(() => {
    clearMenuTimer();
    menuCloseTimer.current = setTimeout(() => {
      setActiveMenuItem(null);
    }, 150); // 150ms delay antes de cerrar
  }, [clearMenuTimer]);

  const handleConfigMenuEnter = useCallback(() => {
    clearConfigTimer();
    setIsConfigMenuOpen(true);
  }, [clearConfigTimer]);

  const handleConfigMenuLeave = useCallback(() => {
    clearConfigTimer();
    configCloseTimer.current = setTimeout(() => {
      setIsConfigMenuOpen(false);
    }, 150); // 150ms delay antes de cerrar
  }, [clearConfigTimer]);

  // Handlers existentes
  const handleLogout = useCallback(() => {
    if (isSSO) {
      clearStoredAuth();
      window.location.href = getRexSSOLogoutUrl();
    } else {
      dispatch(logout());
      showNotification('info', translate('auth:logout.success'));
      void navigate(ROUTE_DEFINITIONS.INICIAR_SESION.path, { replace: true });
    }
  }, [dispatch, isSSO, navigate, showNotification, translate]);

  const handleUserMenuClose = () => {
    setIsUserMenuOpen(false);
  };

  const handleAppLauncherClose = useCallback(() => {
    setIsAppLauncherOpen(false);
  }, []);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return {
    // Estados
    activeMenuItem,
    isConfigMenuOpen,
    isMobileMenuOpen,
    isUserMenuOpen,
    isHelpMenuOpen,
    isAppLauncherOpen,
    searchTerm,
    
    // Setters (mantenidos para compatibilidad)
    setActiveMenuItem,
    setIsConfigMenuOpen,
    setIsMobileMenuOpen,
    setIsUserMenuOpen,
    setIsHelpMenuOpen,
    setIsAppLauncherOpen,
    
    // Handlers mejorados
    handleMenuItemEnter,
    handleMenuItemLeave,
    handleConfigMenuEnter,
    handleConfigMenuLeave,
    clearMenuTimer,
    clearConfigTimer,
    
    // Handlers existentes
    handleLogout,
    handleUserMenuClose,
    handleAppLauncherClose,
    handleMobileMenuToggle,
    handleSearchChange,
    
    // Navigation
    navigate,
  };
};
